// Reusable hook for sortable table functionality
// Manages sort state, sorting logic, and provides helper functions for rendering sort icons

import { useState, useMemo } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

export type SortDirection = 'asc' | 'desc' | null;

interface UseSortableTableOptions<T> {
  data: T[];
  initialSortKey?: string | null;
  initialSortDirection?: SortDirection;
}

interface UseSortableTableReturn<T> {
  sortedData: T[];
  sortKey: string | null;
  sortDirection: SortDirection;
  toggleSort: (key: string) => void;
  renderSortIcon: (key: string) => JSX.Element;
}

export function useSortableTable<T extends Record<string, any>>({
  data,
  initialSortKey = null,
  initialSortDirection = null,
}: UseSortableTableOptions<T>): UseSortableTableReturn<T> {
  const [sortKey, setSortKey] = useState<string | null>(initialSortKey);
  const [sortDirection, setSortDirection] = useState<SortDirection>(initialSortDirection);

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      // Cycle through: asc -> desc -> null (no sort)
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortKey(null);
      }
    } else {
      // New sort key, start with ascending
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const sortedData = useMemo(() => {
    if (!sortKey || !sortDirection) {
      return data;
    }

    return [...data].sort((a, b) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortDirection === 'asc' ? 1 : -1;
      if (bValue == null) return sortDirection === 'asc' ? -1 : 1;

      // String comparison (case-insensitive)
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase(), 'fr');
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      // Numeric comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Fallback: convert to string and compare
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      const comparison = aStr.localeCompare(bStr, 'fr');
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [data, sortKey, sortDirection]);

  const renderSortIcon = (key: string): JSX.Element => {
    if (sortKey !== key) {
      return <ArrowUpDown className="w-4 h-4 text-neutral-400" />;
    }

    if (sortDirection === 'asc') {
      return <ArrowUp className="w-4 h-4 text-primary-600" />;
    }

    return <ArrowDown className="w-4 h-4 text-primary-600" />;
  };

  return {
    sortedData,
    sortKey,
    sortDirection,
    toggleSort,
    renderSortIcon,
  };
}
